import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaSearch, FaUtensils, FaConciergeBell, FaBook, FaImages,
  FaUsers, FaHandshake, FaQuoteLeft, FaInfoCircle, FaImage,
  FaLeaf, FaChartBar, FaStar, FaSpinner
} from 'react-icons/fa';
import './SearchResults.css';

const CATEGORY_META = {
  professionals: { label: 'Professionals', icon: <FaUtensils />, path: '/professionals' },
  services:      { label: 'Services',      icon: <FaConciergeBell />, path: '/services' },
  recipes:       { label: 'Recipes',       icon: <FaBook />, path: '/recipes' },
  gallery:       { label: 'Gallery',       icon: <FaImages />, path: '/gallery' },
  team:          { label: 'Team',          icon: <FaUsers />, path: '/about' },
  partners:      { label: 'Partners',      icon: <FaHandshake />, path: '/' },
  testimonials:  { label: 'Testimonials',  icon: <FaQuoteLeft />, path: '/' },
  about:         { label: 'About Page',    icon: <FaInfoCircle />, path: '/about' },
  hero:          { label: 'Home Slides',   icon: <FaImage />, path: '/' },
  whychoose:     { label: 'Why Choose Us', icon: <FaLeaf />, path: '/' },
  stats:         { label: 'Stats',         icon: <FaChartBar />, path: '/' }
};

/**
 * Attaches an IntersectionObserver to a ref.
 * Adds the "is-visible" class when the element scrolls into view.
 * Re-arms itself whenever `deps` change (so tab switches replay it).
 */
function useRevealOnScroll(deps = []) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reset so it can animate again on new content / tab switch
    el.classList.remove('is-visible');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, deps);

  return ref;
}

function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get('q') || '';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [input, setInput] = useState(query);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    setInput(query);
    if (!query.trim()) {
      setData(null);
      return;
    }
    setLoading(true);
    axios.get(`/api/search?q=${encodeURIComponent(query)}`)
      .then(r => setData(r.data))
      .catch(() => setData({ q: query, total: 0, results: {} }))
      .finally(() => setLoading(false));
    setActiveTab('all');
  }, [query]);

  const submit = (e) => {
    e.preventDefault();
    const term = input.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const imgSrc = (row) => {
    const raw = row.image || row.logo;
    if (!raw) return 'https://via.placeholder.com/160x120?text=No+Image';
    if (raw.startsWith('http')) return raw;
    return `http://localhost:4000${raw}`;
  };

  const results = data?.results || {};
  const categoriesWithResults = Object.keys(results).filter(k => results[k]?.length > 0);
  const visibleCategories = activeTab === 'all'
    ? categoriesWithResults
    : categoriesWithResults.filter(k => k === activeTab);

  /* ---- Refs (each one scroll-reveals independently) ---- */
  const heroRef    = useRevealOnScroll([query]);
  const loadingRef = useRevealOnScroll([loading]);
  const emptyRef   = useRevealOnScroll([query, data?.total]);
  const tabsRef    = useRevealOnScroll([activeTab, data?.total]);
  const startRef   = useRevealOnScroll([query]);

  return (
    <div className="search-root">
      {/* HERO */}
      <div ref={heroRef} className="reveal search-hero">
        <h1><FaSearch /> Search Results</h1>
        <form className="search-bar" onSubmit={submit}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search professionals, recipes, services, gallery…"
            autoFocus
          />
          <button type="submit"><FaSearch /> Search</button>
        </form>

        {query && !loading && data && (
          <p className="search-meta">
            {data.total > 0
              ? <>Found <b>{data.total}</b> result{data.total !== 1 && 's'} for "<b>{query}</b>"</>
              : <>No results found for "<b>{query}</b>"</>}
          </p>
        )}
      </div>

      {/* LOADING */}
      {loading && (
        <div ref={loadingRef} className="reveal search-loading">
          <FaSpinner className="search-spinner" /> Searching…
        </div>
      )}

      {/* NO RESULTS */}
      {!loading && data && data.total === 0 && (
        <div ref={emptyRef} className="reveal search-empty">
          <FaSearch className="search-empty-icon" />
          <h2>Nothing matched "<b>{query}</b>"</h2>
          <p>Try a different keyword, or check the spelling.</p>
          <div className="search-empty-suggestions">
            <span>Try:</span>
            <button onClick={() => navigate('/search?q=chef')}>chef</button>
            <button onClick={() => navigate('/search?q=housekeeper')}>housekeeper</button>
            <button onClick={() => navigate('/search?q=cleaner')}>cleaner</button>
            <button onClick={() => navigate('/search?q=recipe')}>recipe</button>
          </div>
        </div>
      )}

      {/* RESULTS */}
      {!loading && data && data.total > 0 && (
        <>
          {/* TABS */}
          <div ref={tabsRef} className="reveal search-tabs">
            <button
              className={activeTab === 'all' ? 'active' : ''}
              onClick={() => setActiveTab('all')}
            >
              All ({data.total})
            </button>
            {categoriesWithResults.map(k => (
              <button
                key={k}
                className={activeTab === k ? 'active' : ''}
                onClick={() => setActiveTab(k)}
              >
                {CATEGORY_META[k]?.icon} {CATEGORY_META[k]?.label} ({results[k].length})
              </button>
            ))}
          </div>

          {/* CATEGORIES */}
          <div className="search-results">
            {visibleCategories.map((key) => {
              const meta = CATEGORY_META[key] || { label: key, icon: <FaSearch /> };
              return (
                <CategorySection
                  key={key}
                  categoryKey={key}
                  meta={meta}
                  rows={results[key]}
                  activeTab={activeTab}
                  imgSrc={imgSrc}
                />
              );
            })}
          </div>
        </>
      )}

      {/* START TYPING */}
      {!query && (
        <div ref={startRef} className="reveal search-empty">
          <FaSearch className="search-empty-icon" />
          <h2>Start typing to search the whole site</h2>
          <p>Professionals, services, recipes, gallery, team, about content — everything in one place.</p>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Each category reveals itself + staggers its own cards
   ========================================================= */
function CategorySection({ categoryKey, meta, rows, activeTab, imgSrc }) {
  const sectionRef = useRevealOnScroll([categoryKey, activeTab, rows.length]);

  return (
    <section ref={sectionRef} className="reveal search-category">
      <header className="search-category-head">
        <h2>{meta.icon} {meta.label}</h2>
        <span>{rows.length} result{rows.length !== 1 && 's'}</span>
      </header>

      <div className="search-cards">
        {rows.map((row, cardIndex) => {
          const showImage = ['professionals', 'recipes', 'gallery', 'team', 'partners', 'hero', 'services'].includes(categoryKey);
          return (
            <div
              key={`${categoryKey}-${row.id}`}
              className="search-card"
              style={{ transitionDelay: `${Math.min(cardIndex * 70, 420)}ms` }}
            >
              {showImage && (
                <img
                  src={imgSrc(row)}
                  alt={row.title || row.name}
                  className="search-card-img"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/160x120?text=No+Image'; }}
                />
              )}
              <div className="search-card-body">
                <h3>{row.title || row.label || '-'}</h3>
                {row.subtitle && <p className="search-card-sub">{row.subtitle}</p>}
                {row.description && (
                  <p className="search-card-desc">
                    {String(row.description).slice(0, 130)}
                    {String(row.description).length > 130 && '…'}
                  </p>
                )}
                {row.avg_rating > 0 && (
                  <p className="search-card-rating">
                    <FaStar /> {Number(row.avg_rating).toFixed(1)}
                  </p>
                )}
                {row.section && (
                  <span className="search-card-tag">{row.section}</span>
                )}
              </div>
              <Link to={meta.path} className="search-card-link">
                View →
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default SearchResults;
